import React, { useState, useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import useOccupations from './Occupations.services';
import OccupationSubareaRow from './OccupationSubareaRow';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const emptyArea = { id: null, key: '', name: '' };

const OccupationsCatalog = () => {

  const {
    occupationAreas,
    getOccupations,
    saveOccupationArea,
    deleteOccupationArea,
    saveOccupationSubarea,
    deleteOccupationSubarea,
    updateSuccess,
    updateFailure,
    handleClose,
  } = useOccupations();

  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [areaEditing, setAreaEditing] = useState(emptyArea);

  const [addingSubareaForAreaId, setAddingSubareaForAreaId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    getOccupations();
  }, []);

  const openNewArea = () => {
    setAreaEditing(emptyArea);
    setAreaDialogOpen(true);
  };

  const openEditArea = (event, area) => {
    event.stopPropagation();
    setAreaEditing(area);
    setAreaDialogOpen(true);
  };

  const askDeleteArea = (event, area) => {
    event.stopPropagation();
    setDeleteTarget({ type: 'area', item: area });
  };

  return (
    <Box>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Catálogo de ocupaciones
          </Typography>
          <Button startIcon={<AddIcon />} onClick={openNewArea}>
            Agregar área
          </Button>
        </Box>

        {occupationAreas.length === 0 && (
          <Typography color="text.secondary">No hay áreas registradas</Typography>
        )}

        {occupationAreas.map((area) => (
          <Accordion key={area.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 1 }}>
                <Box>
                  <Typography variant="subtitle1">{area.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{area.key}</Typography>
                </Box>
                <Box onClick={(event) => event.stopPropagation()}>
                  <IconButton edge="end" aria-label="editar área" onClick={(event) => openEditArea(event, area)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="eliminar área" onClick={(event) => askDeleteArea(event, area)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setAddingSubareaForAreaId(area.id)}
                  disabled={addingSubareaForAreaId === area.id}
                >
                  Agregar ocupación
                </Button>
              </Box>
              <List>
                {area.subareas.length === 0 && addingSubareaForAreaId !== area.id && (
                  <Typography color="text.secondary">No hay ocupaciones registradas para esta área</Typography>
                )}
                {area.subareas.map((subarea) => (
                  <OccupationSubareaRow
                    key={subarea.id}
                    subarea={subarea}
                    onSave={(draft) => saveOccupationSubarea({ ...subarea, ...draft, occupationAreaId: area.id })}
                    onDelete={(item) => setDeleteTarget({ type: 'subarea', item })}
                  />
                ))}
                {addingSubareaForAreaId === area.id && (
                  <OccupationSubareaRow
                    subarea={null}
                    startInEditMode
                    onSave={(draft) => saveOccupationSubarea({ ...draft, occupationAreaId: area.id })}
                    onCancel={() => setAddingSubareaForAreaId(null)}
                  />
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Area dialog */}
      <Dialog open={areaDialogOpen} onClose={() => setAreaDialogOpen(false)}>
        <DialogTitle>{areaEditing.id ? 'Editar área' : 'Nueva área'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Las áreas agrupan a las ocupaciones del catálogo.
          </DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            id="area-key"
            label="Clave"
            fullWidth
            variant="standard"
            value={areaEditing.key}
            onChange={(event) => {
              setAreaEditing({ ...areaEditing, key: event.target.value });
            }}
          />
          <TextField
            required
            margin="dense"
            id="area-name"
            label="Nombre"
            fullWidth
            variant="standard"
            value={areaEditing.name}
            onChange={(event) => {
              setAreaEditing({ ...areaEditing, name: event.target.value });
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAreaDialogOpen(false)}>Cancelar</Button>
          <Button
            disabled={!areaEditing.key?.trim() || !areaEditing.name?.trim()}
            onClick={async () => {
              await saveOccupationArea(areaEditing);
              setAreaDialogOpen(false);
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Eliminar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`¿Deseas eliminar "${deleteTarget?.item?.name}"? Esta acción no se puede deshacer.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button
            color="error"
            onClick={async () => {
              if (deleteTarget?.type === 'area') {
                await deleteOccupationArea(deleteTarget.item);
              } else if (deleteTarget?.type === 'subarea') {
                await deleteOccupationSubarea(deleteTarget.item);
              }
              setDeleteTarget(null);
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={updateSuccess} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          Catálogo de ocupaciones actualizado
        </Alert>
      </Snackbar>
      <Snackbar open={updateFailure} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
          Error, no se pudo actualizar el catálogo de ocupaciones
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OccupationsCatalog;
